import { push } from 'react-router-redux';
import moment from 'moment';
import { APP_INIT } from '../helpers/serverRenderingUtils';
import { MPARTICLE_META_TYPE } from './modules/analytics/trackReduxAction';
import { unsetAndRedirectUser } from './user';

export const CLEAR_PODCAST_ANALYTICS =
  '@@podcastAnalytics/CLEAR_PODCAST_ANALYTICS';
export const RECEIVE_ALL_TIME_PODCAST_EPISODE_DOWNLOADS =
  '@@podcastAnalytics/RECEIVE_ALL_TIME_PODCAST_EPISODE_DOWNLOADS';
export const RECEIVE_PODCAST_EPISODE_DOWNLOADS =
  '@@podcastAnalytics/RECEIVE_PODCAST_EPISODE_DOWNLOADS';
export const RECEIVE_DATE_RANGE_END =
  '@@podcastAnalytics/RECEIVE_DATE_RANGE_END';
export const RECEIVE_DATE_RANGE_START =
  '@@podcastAnalytics/RECEIVE_DATE_RANGE_START';
export const RECEIVE_UNIT = '@@podcastAnalytics/RECEIVE_UNIT';
export const RECEIVE_DATE_RANGE_BUTTON_SELECT =
  '@@podcastAnalytics/RECEIVE_DATE_RANGE_BUTTON_SELECT';
export const RECEIVE_PODCAST_DOWNLOADS_FETCH_REQUEST =
  '@@podcastAnalytics/RECEIVE_PODCAST_DOWNLOADS_FETCH';

const emptyArray = [];
const emptyObject = {};

export const MAX_NUMBER_UNITS = 300;
export const UNITS = {
  DAY: 'day',
  HOUR: 'hour',
  WEEK: 'week',
  MONTH: 'month',
};

// 'past 6 months'
const { weekEnd, weekStart } = generateLastSixMonthsDateRange();
export const DEFAULT_DATE_RANGE_START = weekStart;
export const DEFAULT_DATE_RANGE_END = weekEnd;

const initialState = {
  allTimePodcastDownloads: emptyObject,
  isFetchingDownloads: false,
  dateRangeEnd: DEFAULT_DATE_RANGE_END,
  dateRangeStart: DEFAULT_DATE_RANGE_START,
  podcastEpisodeDownloads: emptyObject,
  rawPodcastEpisodeDownloads: emptyArray,
  podcastEpisodeDownloadsGroupedByEpisode: emptyObject,
  rawPodcastEpisodeDownloadsGroupedByEpisode: emptyArray,
  podcastEpisodeDownloadsGroupedByAgent: emptyArray,
  unit: UNITS.MONTH,
  selectedDateButton: null,
};

// reducers

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case APP_INIT: {
      const { dateRangeEnd, dateRangeStart } = state;
      return {
        ...state,
        dateRangeEnd: dateRangeEnd ? new Date(dateRangeEnd) : null,
        dateRangeStart: dateRangeStart ? new Date(dateRangeStart) : null,
      };
    }
    case CLEAR_PODCAST_ANALYTICS: {
      return initialState;
    }
    case RECEIVE_ALL_TIME_PODCAST_EPISODE_DOWNLOADS: {
      return {
        ...state,
        allTimePodcastDownloads: action.payload,
      };
    }
    case RECEIVE_PODCAST_EPISODE_DOWNLOADS: {
      const {
        podcastEpisodeDownloads,
        podcastEpisodeDownloadsGroupedByEpisode,
        podcastEpisodeDownloadsGroupedByAgent,
      } = action.payload;
      return {
        ...state,
        rawPodcastEpisodeDownloads: podcastEpisodeDownloads,
        rawPodcastEpisodeDownloadsGroupedByEpisode: podcastEpisodeDownloadsGroupedByEpisode,
        podcastEpisodeDownloads: mapDownloadsToAxes(podcastEpisodeDownloads),
        podcastEpisodeDownloadsGroupedByEpisode: mapDownloadsGroupedByEpisodeToAxes(
          podcastEpisodeDownloadsGroupedByEpisode
        ),
        podcastEpisodeDownloadsGroupedByAgent,
        isFetchingDownloads: false,
      };
    }
    case RECEIVE_DATE_RANGE_END: {
      const { dateRangeEnd } = action.payload;
      return {
        ...state,
        dateRangeEnd,
      };
    }
    case RECEIVE_DATE_RANGE_START: {
      const { dateRangeStart } = action.payload;
      return {
        ...state,
        dateRangeStart,
      };
    }
    case RECEIVE_UNIT: {
      const { unit } = action.payload;
      return {
        ...state,
        unit,
      };
    }
    case RECEIVE_PODCAST_DOWNLOADS_FETCH_REQUEST: {
      return {
        ...state,
        isFetchingDownloads: true,
      };
    }
    case RECEIVE_DATE_RANGE_BUTTON_SELECT: {
      const { selectedDateButton } = action.payload;
      return {
        ...state,
        selectedDateButton,
      };
    }
    default:
      return state;
  }
}

// action creators

function receivePodcastEpisodeDownloads({
  podcastEpisodeDownloads = emptyArray,
  podcastEpisodeDownloadsGroupedByEpisode = emptyObject,
  podcastEpisodeDownloadsGroupedByAgent = emptyArray,
}) {
  return {
    type: RECEIVE_PODCAST_EPISODE_DOWNLOADS,
    payload: {
      podcastEpisodeDownloads,
      podcastEpisodeDownloadsGroupedByEpisode,
      podcastEpisodeDownloadsGroupedByAgent,
    },
  };
}

export function clearPodcastGraphAnalytics() {
  return {
    type: CLEAR_PODCAST_ANALYTICS,
  };
}

function receiveAllTimePodcastEpisodeDownloads({ ct = 0, averageCt = 0 }) {
  return {
    type: RECEIVE_ALL_TIME_PODCAST_EPISODE_DOWNLOADS,
    payload: {
      count: ct,
      averageCount: averageCt,
    },
  };
}

export function receiveDateRangeButtonSelect(selectedDateButton) {
  return {
    type: RECEIVE_DATE_RANGE_BUTTON_SELECT,
    payload: {
      selectedDateButton,
    },
  };
}

export function receiveDateRangeEnd(dateRangeEnd) {
  return {
    type: RECEIVE_DATE_RANGE_END,
    payload: {
      dateRangeEnd,
    },
  };
}

export function receiveDateRangeStart(dateRangeStart) {
  return {
    type: RECEIVE_DATE_RANGE_START,
    payload: {
      dateRangeStart,
    },
  };
}

function receiveUnit(unit) {
  return {
    type: RECEIVE_UNIT,
    payload: {
      unit,
    },
  };
}

export function receivePodcastDownloadsFetchRequest() {
  return {
    type: RECEIVE_PODCAST_DOWNLOADS_FETCH_REQUEST,
  };
}

// thunks

export function fetchPodcastEpisodeDownloads(podcastEpisodeId, newUnit) {
  return (dispatch, getState) => {
    const {
      podcastAnalytics: { dateRangeStart, dateRangeEnd, unit },
    } = getState();
    let requestUnit = unit;
    if (newUnit) {
      requestUnit = newUnit;
      dispatch(receiveUnit(newUnit));
    }

    dispatch(receivePodcastDownloadsFetchRequest());
    // dateRangeStart, dateRangeEnd can be JS Date objects or moment instances
    const start = getTimeFromMomentOrDateObject(dateRangeStart);
    const end = getTimeFromMomentOrDateObject(dateRangeEnd);
    let url = `/api/podcastepisode/${podcastEpisodeId}/analytics/downloads`;
    if (start && end) {
      url = `${url}?dateRangeStart=${start}&dateRangeEnd=${end}`;
      url = `${url}&utcOffsetStart=${new Date(start).getTimezoneOffset()}`;
      url = `${url}&utcOfxfsetEnd=${new Date(end).getTimezoneOffset()}`;
      url = `${url}&unit=${requestUnit}`;
    }
    return fetch(url, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            dispatch(unsetAndRedirectUser());
            return;
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        dispatch(receivePodcastEpisodeDownloads(responseJson));
        return responseJson;
      });
  };
}

export function fetchMyPodcastDownloads(newUnit) {
  return (dispatch, getState) => {
    const {
      podcastAnalytics: { dateRangeStart, dateRangeEnd, unit },
    } = getState();
    let requestUnit = unit;
    if (newUnit) {
      requestUnit = newUnit;
      dispatch(receiveUnit(newUnit));
    }
    dispatch(receivePodcastDownloadsFetchRequest());
    // dateRangeStart, dateRangeEnd can be JS Date objects or moment instances
    const start = getTimeFromMomentOrDateObject(dateRangeStart);
    const end = getTimeFromMomentOrDateObject(dateRangeEnd);

    let url = '/api/podcast/analytics/downloads';
    if (start && end) {
      url = `${url}?dateRangeStart=${start}&dateRangeEnd=${end}`;
      url = `${url}&utcOffsetStart=${new Date(start).getTimezoneOffset()}`;
      url = `${url}&utcOffsetEnd=${new Date(end).getTimezoneOffset()}`;
      url = `${url}&unit=${requestUnit}`;
    }
    return fetch(url, {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        if (response.status === 401) {
          // not logged in
          dispatch(unsetAndRedirectUser());
          return;
        }
        // e.g. no permissions (403)
        dispatch(push('/404'));
      })
      .then(responseJson => {
        dispatch(receivePodcastEpisodeDownloads(responseJson));
        return responseJson;
      });
  };
}

export function fetchMyAllTimePodcastDownloads() {
  return (dispatch, getState) =>
    // TODO: another loading state?
    fetch('/api/podcast/analytics/downloads/all', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        if (response.status === 401) {
          // not logged in
          dispatch(unsetAndRedirectUser());
          return;
        }
        // e.g. no permissions (403)
        dispatch(push('/404'));
      })
      .then(responseJson => {
        dispatch(receiveAllTimePodcastEpisodeDownloads(responseJson || {}));
        return responseJson;
      });
}

function generateLastSixMonthsDateRange() {
  const weekEnd = moment()
    .startOf('day')
    .toDate();
  const weekStart = moment()
    .subtract(6, 'months')
    .toDate();
  return {
    weekEnd,
    weekStart,
  };
}

function getTimeFromMomentOrDateObject(dateObj) {
  return dateObj && (dateObj.valueOf ? dateObj.valueOf() : dateObj.getTime());
}

function mapDownloadsToAxes(downloads) {
  const categories = [];
  const data = [];
  downloads.forEach(row => {
    const utcDate = new Date(row.unit);
    categories.push(utcDate);
    data.push([utcDate.getTime(), row.ct]);
  });
  return {
    categories,
    series: [{ data }],
  };
}

function mapDownloadsGroupedByEpisodeToAxes(downloads) {
  const categories = [];
  const data = [];
  const l = downloads.length;
  for (let i = 0; i < l; i++) {
    const row = downloads[i];
    categories.push({
      episodeIndex: i,
      title: row.title,
      created: row.created,
    });
    data.push(row.ct);
  }
  return {
    categories,
    series: [{ data }],
  };
}
